import type { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { IconChevronLeft, IconChevronRight } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

/**
 * The building blocks of the user panel's right-hand column — Figma
 * `panel-list`. Every row is `pb-16` with a bottom rule except the last one.
 */

export function PanelHeader({
  title,
  onBack,
  compact = false,
}: {
  title: string
  onBack?: () => void
  /** Sub-page header: 4px gap and 12px bottom padding (Figma 2276:299704). */
  compact?: boolean
}): ReactElement {
  const { t } = useTranslation()

  return (
    <div
      data-name="panel-list"
      className={cn(
        'hub-rule flex w-full',
        compact ? 'items-center gap-1 pb-3' : 'items-start gap-2 pb-4',
      )}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label={t('actions.back')}
          className="text-btn-icon hover:bg-btn-secondary-raised dark:hover:bg-panel-sidebar flex shrink-0 cursor-pointer items-center justify-center rounded-xs p-1.5 transition-colors"
        >
          <IconChevronLeft className="size-4" aria-hidden="true" />
        </button>
      )}
      <h2 className="hub-headline-lg text-list-text whitespace-nowrap">{title}</h2>
    </div>
  )
}

export function PanelRow({
  children,
  last = false,
  interactive = false,
  about = false,
  className,
}: {
  children: ReactNode
  last?: boolean
  /** Highlights the whole row on hover when it acts as a single target. */
  interactive?: boolean
  about?: boolean
  className?: string
}): ReactElement {
  return (
    <div
      data-name="panel-list"
      className={cn(
        'flex w-full items-center gap-4 pb-4',
        !last && 'hub-rule',
        // Background only — no margin tricks, so the row keeps its exact
        // Figma geometry and the separator stays flush.
        interactive && !about && 'hover:bg-btn-secondary-raised/50',
        className,
      )}
    >
      {children}
    </div>
  )
}

/** Label on the left, value on the right — both `title/lg`. */
export function PanelValueRow({
  label,
  value,
  last,
}: {
  label: string
  value: ReactNode
  last?: boolean
}): ReactElement {
  return (
    <PanelRow {...(last === undefined ? {} : { last })}>
      <div className="flex min-w-px flex-1 items-start justify-between gap-4">
        <span className="hub-title-lg text-list-text shrink-0">{label}</span>
        <span className="hub-title-lg text-list-text-secondary text-right">{value}</span>
      </div>
    </PanelRow>
  )
}

/** A bold figure with a trailing unit, e.g. **28** cr. */
export function CreditValue({ amount, suffix }: { amount: string; suffix: string }): ReactElement {
  return (
    <>
      <span className="hub-title-lg-highlight text-list-text">{amount}</span>
      <span>{suffix}</span>
    </>
  )
}

/**
 * Label plus a chevron. The whole row is the hit target — the chevron is only
 * the affordance, so pointer and keyboard users get the same target size.
 */
export function PanelLinkRow({
  label,
  onOpen,
  last,
}: {
  label: string
  onOpen?: () => void
  last?: boolean
}): ReactElement {
  return (
    <PanelRow {...(last === undefined ? {} : { last })} interactive={Boolean(onOpen)} about>
      <button
        type="button"
        aria-label={label}
        onClick={onOpen}
        className="flex w-full cursor-pointer items-center gap-4 text-left"
      >
        <span className="hub-title-lg text-list-text min-w-px flex-1">{label}</span>
        <span className="text-btn-icon flex shrink-0 items-center justify-center p-1.5">
          <IconChevronRight className="size-4" aria-hidden="true" />
        </span>
      </button>
    </PanelRow>
  )
}

/**
 * Label plus a `button-sm` showing the current value and a chevron.
 * The whole row is clickable, not just the value chip.
 */
export function PanelChoiceRow({
  label,
  value,
  onOpen,
  last,
}: {
  label: string
  value: string
  onOpen?: () => void
  last?: boolean
}): ReactElement {
  return (
    <PanelRow {...(last === undefined ? {} : { last })} interactive={Boolean(onOpen)}>
      <button
        type="button"
        aria-label={label}
        onClick={onOpen}
        className="flex w-full items-center gap-4 text-left"
      >
        <span className="hub-title-lg text-list-text min-w-px flex-1">{label}</span>
        <span className="flex shrink-0 items-center justify-center gap-2 py-1 pr-1.5 pl-4">
          <span className="hub-label-md text-list-text-secondary truncate">{value}</span>
          <IconChevronRight className="text-btn-icon size-[16px] shrink-0" aria-hidden="true" />
        </span>
      </button>
    </PanelRow>
  )
}
